import { Profile } from "../../../../core/models/entities/Profile";
import {
  BaseLogo,
  GetOverallApprovalStatus,
  getLogoOrgId,
  ImageLogo,
  LogoEditStatus,
  LogoType,
} from "../../../../core/models/entities/BaseLogo";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { getLogoColumnsConfig } from "../const/logoColumnsConfig";
import { LogoRowItem } from "../components/logo-list-item.component";
import {
  FilterRecipe,
  FilterChangeEvent,
} from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { Role } from "../../../../core/models/entities/Role";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { organizationFilterField } from "../../../shared/const/organizationFilterField";
import {
  Color,
  Font,
  LogoOptions,
  TransferLogoType,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { useMultiSelectStateMachine } from "../../../shared/hooks/useMultiSelectStateMachine";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useOrderabilityGuardActive } from "../../../../hooks/useOrderabilityGuardActive";
import { ReactNode, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enviroment } from "../../../../enviroment";
import {
  LOGO_METHOD_FILTER_KEY,
  SCREEN_PRINT_METHOD_FILTER_VALUE,
  getMethodLabel,
  getStatusLabel,
  getTypeLabel,
  isLogoListI18nOn,
  mergeScreenPrintMethods,
  rewriteScreenPrintMethodFilter,
} from "../../../../utils/functions/logoListI18n";
import {
  LOGOS_NONE_ORDERABLE_KEY,
  noneOrderableLogos,
} from "../../../../utils/functions/orderability";
import { MultiDeleteLogosDialogs } from "../components/multi-delete-logos-dialogs.component";
import { useNavigate } from "react-router";
import EmailDialog from "../../../components/Approval/EmailDialog";
import { useApiClient } from "../../../../data/clients/ApiClient";
// Removed performCavemanReFetch import - no longer needed with optimistic updates
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { buildLogoColorFilterRecipe } from "../models/logoColorFilter";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFolder,
  faPlus,
  faShare,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { AddToCartIcon, TrashcanCrossIcon } from "../../../../resources/icons/Icons";
import {
  useErrorToast,
  useSuccessToast,
  withToasts,
} from "../../../components/Toast/Toast";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import {
  countLabel,
  RequireSelection,
} from "../../../components/RequireSelection/RequireSelection";
import { DesignFolder } from "../../../../core/models/entities/DesignFolder";
import {
  FolderBar,
  flattenFolders,
  LOGO_DRAG_MIME,
  UNFILED_FOLDER_ID,
} from "../../../components/FolderBar/folder-bar.component";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { LogoPdfGenerator } from "../../../../utils/functions/pdf-generator.service";
import { usePdfDownloadWithSharpGate } from "../../../../hooks/usePdfDownloadWithSharpGate";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { useSalesMarkup } from "../../../../hooks/useSalesMarkup";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { isDanishLanguage } from "../../../../i18n";
import { colorPickerLabel } from "../../../../utils/systemColors";

export function useFunctionalListLogosViewModel(
  profile: Profile,
  isPageLoading: boolean,
  lastPage: number,
  options: LogoOptions,
  query: PageQuery,
  queryChanger: QueryChanger,
  onSelectLogo: (logo: BaseLogo) => void,
  onViewRejectNote: (logo: BaseLogo) => void,
  onOpenInitApproval: (logo: BaseLogo) => void,
  openCreateLogoDialog: () => void,
  customers?: Organization[],
  vendors?: Organization[],
  logos?: BaseLogo[],
  updatingLogoId?: number,
  // keepPreviousData indicators (see logo-list.screen.tsx): forwarded to
  // FunctionalList so stale rows are dimmed while a new query loads.
  isFetching?: boolean,
  isPreviousData?: boolean,
): TableData<BaseLogo> & { folderBar?: ReactNode } {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    handleGoToEditLogo,
    handleDeleteLogo,
    handlePlaceVerdict,
    handleAddToCart,
    handleAddItemsToCart,
    handleRejectAndDelete,
    handleRejectAndEdit,
    isLoading: isActionRunning,
  } = useLogoActionHandler();

  const { role, organizationId } = useProfileContext();
  const orderabilityGuardActive = useOrderabilityGuardActive();
  const { hidePrices, showCustomerPrices } = usePriceVisibility();
  const { active: salesPriceActive } = useSalesMarkup();
  const { generatePublicKeys, sendMultiApprovalEmail } = useApiClient();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { designFolderRepository } = useRepositoriesContext();

  const {
    isMultiSelect,
    toggleMultiSelect,
    selectedData,
    onRowChecked,
    onMultiSelectRow,
    resetSelection,
  } = useMultiSelectStateMachine<BaseLogo>({ onSelect: onSelectLogo });

  // --- Folders (fase 1: Vendor/Admin only; customers see the list exactly as
  // before - no folder bar, no folders request). ONE folder tree per org,
  // shared with the design list - only the shown count differs (logoCount).
  const isFolderUser = role === Role.Admin || role === Role.Vendor;
  const { data: folders } = designFolderRepository.useFolders(
    isFolderUser ? organizationId : undefined,
  );

  // The folder a user is standing in IS a list filter: it flows through the
  // existing filterByField/filterByValue plumbing (pageQuery.ts), so backend,
  // caching and keepPreviousData work unchanged. No filter entry = root
  // ("All logos", the unchanged default view); value '0' = Unfiled.
  const folderFilterField = "folder.id";
  const activeFolderFilter = query.filterBy?.find(
    (f) => f.field === folderFilterField,
  );
  const currentFolderId = activeFolderFilter
    ? Number(activeFolderFilter.value)
    : null;

  function navigateToFolder(folderId: number | null) {
    const otherFilters = (query.filterBy ?? []).filter(
      (f) => f.field !== folderFilterField,
    );
    const newFilters =
      folderId === null
        ? otherFilters
        : [...otherFilters, { field: folderFilterField, value: String(folderId) }];
    queryChanger.setPageQuery({
      ...query,
      filterBy: newFilters,
      page: { ...query.page, index: 1 },
    });
  }

  // ListHeader owns its own filter state and replaces the whole filter list on
  // every change - it never knows about the folder filter (folder navigation
  // lives outside the filter bar). Re-attach the current folder filter here so
  // applying/clearing header filters never kicks the user out of a folder.
  function commitFilters(activeFilters: FilterChangeEvent["activeFilters"]) {
    const withoutFolder = activeFilters.filter(
      (f) => f.key !== folderFilterField,
    );
    const merged = activeFolderFilter
      ? [
          ...withoutFolder,
          { key: folderFilterField, value: activeFolderFilter.value },
        ]
      : withoutFolder;
    queryChanger.handleFilterChange({ activeFilters: merged });
  }

  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [isMoving, setIsMoving] = useState(false);

  async function moveLogosToFolder(
    logoIds: number[],
    target: DesignFolder | null,
  ) {
    if (!organizationId || logoIds.length === 0 || isMoving) return;
    setIsMoving(true);
    try {
      await designFolderRepository.moveLogosToFolder(
        organizationId,
        logoIds,
        target?.id ?? null,
      );
      useSuccessToast(
        t("logoList.movedLogosToFolder", {
          count: logoIds.length,
          folder: target ? target.name : t("logoList.unfiled"),
          interpolation: { escapeValue: false },
        }),
      );
      resetSelection();
    } catch {
      useErrorToast(t("logoList.moveLogosFailed"));
    } finally {
      setIsMoving(false);
    }
  }

  function handleLogoDragStart(
    logo: BaseLogo,
    e: React.DragEvent<HTMLDivElement>,
  ) {
    // Dragging a row that is part of the current selection drags the whole
    // selection; dragging an unselected row drags just that logo. Stock logos
    // (isForAllCustomers) are excluded - they are shared across organizations
    // and cannot be filed into one org's folder (backend rejects the batch).
    const selectedMovable = Object.values<BaseLogo>(selectedData).filter(
      (l) => !l.isForAllCustomers,
    );
    const selectedIds = selectedMovable.map((l) => l.id);
    const ids = selectedIds.includes(logo.id)
      ? selectedIds
      : logo.isForAllCustomers
        ? []
        : [logo.id];
    if (ids.length === 0) return;
    e.dataTransfer.setData(LOGO_DRAG_MIME, JSON.stringify(ids));
    e.dataTransfer.setData("text/plain", ids.join(","));
    e.dataTransfer.effectAllowed = "move";
  }

  async function handleCreateFolder(name: string) {
    if (!organizationId) return;
    try {
      await designFolderRepository.createFolder(
        organizationId,
        name,
        currentFolderId === UNFILED_FOLDER_ID ? null : currentFolderId,
      );
    } catch (error) {
      useErrorToast(t("logoList.createFolderFailed"));
      throw error; // FolderBar keeps the input open for a retry
    }
  }

  async function handleRenameFolder(folder: DesignFolder, newName: string) {
    if (!organizationId) return;
    try {
      await designFolderRepository.renameFolder(
        organizationId,
        folder.id,
        newName,
      );
    } catch (error) {
      useErrorToast(t("logoList.renameFolderFailed"));
      throw error; // FolderBar keeps the input open for a retry
    }
  }

  async function handleDeleteFolder(folder: DesignFolder) {
    if (!organizationId) return;
    try {
      await designFolderRepository.deleteFolder(organizationId, folder.id);
    } catch {
      // Backend answers 409/400 when the folder still contains items.
      useErrorToast(t("logoList.folderNotEmpty"));
    }
  }

  // Re-parent a folder (parentId null = top level). Self/descendant/no-op
  // guards already ran inside the FolderBar.
  async function handleMoveFolder(
    folder: DesignFolder,
    parentId: number | null,
  ) {
    if (!organizationId) return;
    try {
      await designFolderRepository.moveFolder(
        organizationId,
        folder.id,
        parentId,
      );
      const targetName =
        parentId !== null
          ? folders?.find((f) => f.id === parentId)?.name
          : undefined;
      useSuccessToast(
        targetName
          ? t("logoList.movedFolderTo", {
              name: folder.name,
              target: targetName,
              interpolation: { escapeValue: false },
            })
          : t("logoList.movedFolderToTop", {
              name: folder.name,
              interpolation: { escapeValue: false },
            }),
      );
    } catch {
      useErrorToast(t("logoList.moveFolderFailed"));
    }
  }

  // Persist a new sibling order for one level (parentId null = top level).
  // The FolderBar renders the new order optimistically and falls back to the
  // (refetched) server order once this resolves - success or failure.
  async function handleReorderFolders(
    parentId: number | null,
    orderedIds: number[],
  ) {
    if (!organizationId) return;
    try {
      await designFolderRepository.reorderFolders(
        organizationId,
        parentId,
        orderedIds,
      );
    } catch {
      useErrorToast(t("logoList.reorderFoldersFailed"));
    }
  }

  // Email dialog state for multi-select Send Preview
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState("");

  // Loading state for PDF download
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: bounded wait for sharp logo images
  // + a dialog instead of a silently blurred PDF. Flag off = a plain
  // withToasts around the generator (as before) and a null dialog.
  const { downloadPdf, sharpGateDialog } = usePdfDownloadWithSharpGate({
    colorHex: profile?.whiteLabelConfigurations?.colorHex,
  });

  // Loading state for sending logos via email
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Loading state for the admin-only global-logos CSV export
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Multi-delete for logos (flag REACT_APP_MULTI_DELETE_LOGOS_ENABLED):
  // dialog step (0 closed / 1 neutral confirm / 2 red final confirm) + run
  // state. Mirrors the design list's multi-delete.
  const [multiDeleteStep, setMultiDeleteStep] = useState<0 | 1 | 2>(0);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const [multiDeleteProgress, setMultiDeleteProgress] = useState(0);

  function handleShareClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleShareClose() {
    setAnchorEl(null);
  }

  // Validation function to check if user can perform bulk actions with current selection
  function validateSelectionCount(
    selectedCount: number,
    actionName: string,
  ): boolean {
    if (role !== Role.Admin && selectedCount > 10) {
      useErrorToast(t("logoList.selectionLimit", { action: actionName }));
      return false;
    }
    return true;
  }

  function addToCart() {
    handleAddItemsToCart(Object.values<BaseLogo>(selectedData), 10, orderabilityGuardActive);
  }

  function sendPreview() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(selectedCount, t("logoList.actionSend")) ||
      isSendingEmail
    ) {
      return;
    }
    setEmailDialogOpen(true);
  }

  async function handleDownloadPdf() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(selectedCount, t("logoList.actionDownload")) ||
      isDownloadingPdf
    ) {
      return;
    }

    setIsDownloadingPdf(true);
    const selectedLogos = Object.values<BaseLogo>(selectedData);
    const vendorLogoUrl = profile.whiteLabelConfigurations?.logoUrl;

    // Prepare logo IDs with organization IDs for high-res fetching
    const logoIds = selectedLogos.map((logo) => ({
      logoId: logo.id,
      orgId: getLogoOrgId(logo),
    }));

    try {
      await downloadPdf({
        run: (options) =>
          LogoPdfGenerator.generateMultipleLogosWithHighResPdf(
            logoIds,
            apiClient,
            vendorLogoUrl,
            options,
          ),
        loadingMessage: t("logoList.generatingLogosPdf"),
        successMessage: t("logoList.logosPdfDownloaded"),
        errorMessage: t("logoList.logosPdfFailed"),
        // Runs after every PDF that was actually saved - also one started
        // from the sharp-gate dialog. A stopped PDF keeps the selection.
        onDownloaded: resetSelection,
        setRerunBusy: setIsDownloadingPdf,
      });
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to generate logos PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  // Admin-only CSV export of global (stock) logos. The endpoint answers raw
  // text/csv, so this goes straight Response -> Blob -> programmatic
  // <a download> click (no JSON plumbing). authFetch is called WITHOUT extra
  // headers - passing our own headers object would clobber Authorization.
  async function handleExportCsv() {
    if (isExportingCsv) return;
    setIsExportingCsv(true);
    try {
      const response = await apiClient.exportGlobalLogosCsv();
      const blob = await response.blob();
      // Filename from Content-Disposition (global-logos-{yyyyMMdd}.csv); fall
      // back to the same client-side pattern if the header is not exposed
      // through CORS.
      const disposition = response.headers.get("content-disposition") ?? "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const yyyyMMdd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const filename = match?.[1] ?? `global-logos-${yyyyMMdd}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export global logos CSV:", error);
      useErrorToast(t("logoList.exportCsvFailed"));
    } finally {
      setIsExportingCsv(false);
    }
  }

  async function handleSendPreviewWithEmail() {
    const selectedLogos = Object.values<BaseLogo>(selectedData);

    setIsSendingEmail(true);

    async function transaction() {
      // Generate public keys for each selected logo using API
      const response = await generatePublicKeys(
        selectedLogos.map((logo) => logo.id),
      );
      const keysParam = response.publicKeys.join(",");
      const url = `${window.location.origin}/external-approval/logos?keys=${keysParam}`;

      // Send email with preview URL
      await sendMultiApprovalEmail(response.publicKeys, "logo", approvalEmail);

      return response;
    }

    try {
      await withToasts(
        transaction,
        t("logoList.sendingEmailWithLogos"),
        t("logoList.emailsSent"),
        t("logoList.emailWithLogosFailed"),
      );
      setEmailDialogOpen(false);
      resetSelection();
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to send preview email:", error);
    } finally {
      setIsSendingEmail(false);
    }
  }

  function placeVerdict(logo: BaseLogo, verdict: Verdict) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handlePlaceVerdict(logo, verdict);
  }

  const center = undefined; // Center prop is now empty as Create button moved to right

  const selectedCount = Object.values(selectedData)?.length || 0;
  // Flag designOrderabilityGuardEnabled (#724425): with a selection where
  // not ONE logo is orderable (rule §1: status done), "Add to Order (n)" is
  // disabled and explains why (RequireSelection tooltip + toast under the
  // list-fixes flag, MUI Tooltip + toast otherwise - as the design list),
  // instead of a
  // click that would only skip everything. Mixed selection = button active,
  // the handler filters + toasts. Flag off = undefined = today's DOM.
  const noneOrderableSelected =
    orderabilityGuardActive &&
    noneOrderableLogos(Object.values<BaseLogo>(selectedData));
  const addToOrderBlockedHint = noneOrderableSelected
    ? t(LOGOS_NONE_ORDERABLE_KEY)
    : undefined;
  // The bulk "Add to Order (n)" button, shared by both hint wrappers below.
  const addToOrderButton = (
    <PrimaryButton
      width={"fit"}
      className={"px-4"}
      onClick={addToCart}
      disabled={selectedCount === 0 || noneOrderableSelected}
    >
      <AddToCartIcon />
      <span className={"flex-1"}>
        {countLabel(
          selectedCount,
          t("logoList.addToOrderCount", { count: selectedCount }),
          t("logoList.addToOrder"),
        )}
      </span>
    </PrimaryButton>
  );
  // Flat folder list for the "Move to folder" picker (children indented under
  // their parent, depth-first) - shared with the design list.
  const folderOptions = folders ? flattenFolders(folders) : [];

  function handleMoveSelected(target: DesignFolder | null) {
    setMoveMenuAnchor(null);
    // Stock logos (isForAllCustomers) are shared across every organization and
    // cannot be filed into one org's folder - the backend rejects the whole
    // batch, so filter them out here and tell the user instead.
    const selected = Object.values<BaseLogo>(selectedData);
    const movable = selected.filter((l) => !l.isForAllCustomers);
    const skipped = selected.length - movable.length;
    if (skipped > 0) {
      useErrorToast(t("logoList.stockLogosSkipped", { count: skipped }));
    }
    if (movable.length === 0) return;
    moveLogosToFolder(
      movable.map((l) => l.id),
      target,
    ).then();
  }

  // Multi-delete rule: mirror the logo POPUP's DELETE LOGO button exactly
  // (logo-details.component.tsx): EndUsers never see delete (the menu item is
  // hidden for them below), and chain logos (Classification "11") cannot be
  // deleted - those are skipped and said out loud in dialog 1. Everything
  // else deletes regardless of status, same as the popup; the DOUBLE
  // confirmation is the safety net (the design-list precedent, #715).
  const selectedForDelete = Object.values<BaseLogo>(selectedData);
  // Chain logos (Classification 11) and GLOBAL logos (offered to all customers) are
  // never deleted from KitMaker - the API refuses the latter (GlobalLogoDeleteGuardEnabled).
  const deletableSelected = selectedForDelete.filter(
    (l) => String(l.Classification) !== "11" && !l.isForAllCustomers,
  );
  const skippedForDeleteCount =
    selectedForDelete.length - deletableSelected.length;

  function deleteSelected() {
    handleShareClose();
    if (isDeletingSelected) return;
    if (deletableSelected.length === 0) {
      useErrorToast(t("logoList.multiDeleteNoneDeletable"));
      return;
    }
    setMultiDeleteStep(1);
  }

  // Sequential deletion via the SAME endpoint as the popup's delete
  // (DELETE organizations/{orgId}/logos/{logoId}) with per-logo try/catch.
  // Calls the ApiClient directly instead of logoRepository.delete so the
  // ["logos"] cache is invalidated ONCE at the end instead of once per logo
  // (the repository also does per-logo optimistic cache surgery we don't
  // want to repeat N times mid-run).
  async function executeMultiDelete() {
    if (isDeletingSelected) return;
    setIsDeletingSelected(true);
    setMultiDeleteProgress(0);
    const targets = [...deletableSelected];
    let okCount = 0;
    let failedCount = 0;
    for (const logo of targets) {
      try {
        await apiClient.deleteLogoById(getLogoOrgId(logo), logo.id);
        okCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to delete logo ${logo.id}:`, error);
      }
      setMultiDeleteProgress(okCount + failedCount);
    }
    try {
      await queryClient.invalidateQueries({ queryKey: ["logos"], type: "all" });
    } catch {
      // The deletions themselves succeeded; a failed refetch heals on the
      // next visit.
    }
    if (failedCount === 0) {
      useSuccessToast(t("logoList.multiDeleteSuccess", { count: okCount }));
    } else {
      useErrorToast(
        t("logoList.multiDeletePartial", { ok: okCount, failed: failedCount }),
      );
    }
    resetSelection();
    setIsDeletingSelected(false);
    setMultiDeleteStep(0);
  }

  const right = (
    <div className="flex flex-wrap justify-end gap-2">
      {isFolderUser && (
        <>
          <RequireSelection count={selectedCount}>
            <SecondaryButton
              width={"fit"}
              className={"px-4"}
              disabled={selectedCount === 0 || isMoving}
              onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
            >
              <FontAwesomeIcon icon={faFolder} />
              <span className={"flex-1"}>
                {isMoving
                  ? t("logoList.moving")
                  : countLabel(
                      selectedCount,
                      t("logoList.moveSelected", { count: selectedCount }),
                      t("ux.moveSelected"),
                    )}
              </span>
            </SecondaryButton>
          </RequireSelection>
          <Menu
            anchorEl={moveMenuAnchor}
            open={Boolean(moveMenuAnchor)}
            onClose={() => setMoveMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled>{t("logoList.moveToFolder")}</MenuItem>
            <MenuItem onClick={() => handleMoveSelected(null)}>
              <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
              {t("logoList.unfiled")}
            </MenuItem>
            {folderOptions.map(({ folder, depth }) => (
              <MenuItem
                key={folder.id}
                sx={{ paddingLeft: `${16 + depth * 16}px` }}
                onClick={() => handleMoveSelected(folder)}
              >
                <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
                {folder.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {/* Admin-only CSV export of global (stock) logos - pairs with the
          "Global logos only" filter checkbox in the filter bar. */}
      {role === Role.Admin && (
        <SecondaryButton
          width={"fit"}
          className={"px-4 whitespace-nowrap"}
          onClick={handleExportCsv}
          disabled={isExportingCsv}
        >
          <FontAwesomeIcon icon={faDownload} />
          <span className={"flex-1"}>
            {isExportingCsv
              ? t("logoList.exportingCsv")
              : t("logoList.exportCsv")}
          </span>
        </SecondaryButton>
      )}
      <PrimaryButton
        key={"design-create"}
        width={"fit"}
        className={"px-4"}
        onClick={openCreateLogoDialog}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className={"flex-1"}>{t("logoList.createNewLogo")}</span>
      </PrimaryButton>
      {role !== Role.Admin &&
        (enviroment.uxListFixesEnabled || !addToOrderBlockedHint ? (
          // RequireSelection explains the hint (tooltip + toast) under the
          // list-fixes flag; with that flag off it returns the child as-is,
          // so a present hint takes the design list's Tooltip + toast branch
          // below instead. No hint + list fixes off = today's DOM.
          <RequireSelection
            count={selectedCount}
            blockedHint={addToOrderBlockedHint}
          >
            {addToOrderButton}
          </RequireSelection>
        ) : (
          <Tooltip title={addToOrderBlockedHint}>
            <span
              onClick={() => {
                if (selectedCount > 0) {
                  useErrorToast(addToOrderBlockedHint);
                }
              }}
            >
              {addToOrderButton}
            </span>
          </Tooltip>
        ))}
      <SecondaryButton
        width={
          isDownloadingPdf || isSendingEmail
            ? "fit"
            : selectedCount > 0
              ? "xs"
              : "icon"
        }
        className={"px-4 whitespace-nowrap"}
        onClick={handleShareClick}
        disabled={selectedCount === 0 || isDownloadingPdf || isSendingEmail}
      >
        <FontAwesomeIcon icon={faShare} />
        <span className={"flex-1"}>
          {isDownloadingPdf
            ? t("logoList.generating")
            : isSendingEmail
              ? t("logoList.sending")
              : selectedCount > 0
                ? `(${selectedCount})`
                : ""}
        </span>
      </SecondaryButton>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
          <FontAwesomeIcon icon={faDownload} className="mr-2 w-4" />
          {t("logoList.downloadPdf")}
        </MenuItem>
        <MenuItem onClick={sendPreview} disabled={isSendingEmail}>
          <FontAwesomeIcon icon={faEnvelope} className="mr-2 w-4" />
          {t("logoList.sendViaEmail")}
        </MenuItem>
        {/* Multi-delete (flag): destructive item, visually separated from the
            neutral actions above - hidden for EndUser exactly like the
            popup's delete button. */}
        {enviroment.multiDeleteLogosEnabled &&
          role !== Role.EndUser &&
          selectedCount > 0 && (
            <MenuItem
              onClick={deleteSelected}
              disabled={isDeletingSelected}
              sx={{
                color: "#E22F2F",
                borderTop: "1px solid #ECEEF9",
                "&:hover": { backgroundColor: "#FFFAFA" },
              }}
            >
              <span className="mr-2 w-4 flex justify-center">
                <TrashcanCrossIcon />
              </span>
              {t("logoList.deleteSelectedWithCount", { count: selectedCount })}
            </MenuItem>
          )}
      </Menu>
      {sharpGateDialog}
    </div>
  );

  const filters = buildLogoFilterRecipes(
    profile,
    options.transferMethods,
    vendors,
    customers,
  );

  // Now using optimistic updates in LogoRepository - no need for local deleted tracking
  const handleDeleteAndHide = async (logo: BaseLogo) => {
    await handleDeleteLogo(logo, { silent: true });
  };

  // The multi-select checkbox is NEVER disabled by the order guard (flag
  // designOrderabilityGuardEnabled, #724425) - only by the draft rule below,
  // as before the guard. The same checkbox drives Send via email (the
  // external approval mail / proof), Download, move and multi-delete, and
  // the dealer must be able to tick AWAITING logos and send a proof to
  // several at once (Nichlas 16/10, after the guard's first prod day had
  // disabled exactly those rows). The guard lives in the bulk "Add to Order
  // (n)" button (disabled + hint when nothing selected is orderable, filter
  // + skip toast otherwise - useLogoActionsHandler), the row button and the
  // API.
  function isLogoDisabled(logo: BaseLogo): boolean {
    if (logo.logoType === LogoType.ImageLogo) {
      const imageLogo = logo as ImageLogo;
      return imageLogo.status === LogoEditStatus.Draft;
    }
    return false;
  }

  function handleViewDesigns(logo: BaseLogo) {
    navigate(
      `/design?pageSize=25&pageIndex=1&filterBy=designlogos.logoid&filterOn=${logo.id}`,
    );
  }

  const handleFilterChange = (rawEvent: FilterChangeEvent) => {
    // Flag logoListI18nEnabled: the merged "Screen Print" Method option
    // never reaches the API - it becomes typeFilter=DIREK TRYK here (same
    // precedent as the showOnlyOwned rewrite below). Off = same array.
    const event: FilterChangeEvent = {
      ...rawEvent,
      activeFilters: rewriteScreenPrintMethodFilter(
        rawEvent.activeFilters,
        LOGO_METHOD_FILTER_KEY,
        SCREEN_PRINT_METHOD_FILTER_VALUE,
      ),
    };
    if (profile?.userOrganizationInformations?.role === Role.Vendor) {
      const showOnlyOwned = event.activeFilters.find(
        (f) => f.key === "showOnlyOwned",
      );
      const vendorOrgId =
        profile.userOrganizationInformations.organizationId.toString();

      let newFilters = [...event.activeFilters];

      if (showOnlyOwned && showOnlyOwned.value === true) {
        newFilters = newFilters.filter(
          (f) =>
            f.key !== "showOnlyOwned" &&
            f.key !== organizationFilterField &&
            f.key !== "mainOrganizationId",
        );
        newFilters.push({
          key: "mainOrganizationId",
          value: vendorOrgId,
        });
        commitFilters(newFilters);
        return;
      } else {
        newFilters = newFilters.filter((f) => f.key !== "showOnlyOwned");
        newFilters = newFilters.filter(
          (f) => !(f.key === "mainOrganizationId" && f.value === vendorOrgId),
        );
        commitFilters(newFilters);
        return;
      }
    }
    commitFilters(event.activeFilters);
  };

  return {
    tableTitle: t("logo"),
    columns: getLogoColumnsConfig(
      hidePrices,
      showCustomerPrices,
      role === Role.Admin,
      role === Role.Admin,
      salesPriceActive,
    ).map((column) => ({
      ...column,
      displayValue: translateColumnHeader(column.displayValue),
    })),
    onRowClick: onMultiSelectRow,
    data: logos,
    getKey: getKey,
    getRowColor: logoRowBgColor,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    query: query,
    renderRow: (columns, data, index) => (
      <LogoRowItem
        columns={columns}
        logo={data}
        rowIndex={index}
        onPlaceVerdict={placeVerdict}
        onAddToCart={handleAddToCart}
        onEditLogo={handleGoToEditLogo}
        onSeeRejectNote={onViewRejectNote}
        onDeleteLogo={handleDeleteAndHide}
        onRejectAndDelete={handleRejectAndDelete}
        // "Reject and fix it myself" (flag logoRejectAndEditEnabled): the
        // popup's handler (one PUT + the editor); the row only offers it.
        onRejectAndEdit={handleRejectAndEdit}
        onInitiateApproval={onOpenInitApproval}
        onViewDesigns={handleViewDesigns}
        updatingLogoId={updatingLogoId}
        onDragStartLogo={isFolderUser ? handleLogoDragStart : undefined}
      />
    ),
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onNewSort: queryChanger.handleSortByField,
    onNewPage: queryChanger.handleNewPage,
    onFilterChange: handleFilterChange,
    isLoading: isActionRunning || isPageLoading,
    isFetching: isFetching,
    isPreviousData: isPreviousData,
    filters: filters,
    lastPage: lastPage,
    center: center,
    right: right,
    multiSelect: true,
    selectedData: selectedData,
    onRowSelected: onRowChecked,
    isRowDisabled: isLogoDisabled,
    showVendorFilter: true, // Enable vendor filter for logos
    customers: customers, // Pass customers for the customer search filter
    storageKey: "logo-list-filters",
    // Rendered by LogoListWrapper ABOVE the list box. Only for Vendor/Admin
    // (fase 1) and only once the folders query has data - customers and the
    // initial loading state see the list exactly as before.
    folderBar:
      isFolderUser && folders ? (
        <FolderBar
          folders={folders}
          currentFolderId={currentFolderId}
          rootLabel={t("logoList.allLogos")}
          getFolderCount={(f) => f.logoCount}
          dragMime={LOGO_DRAG_MIME}
          onNavigate={navigateToFolder}
          onCreateFolder={handleCreateFolder}
          onRenameFolder={handleRenameFolder}
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={handleMoveFolder}
          onReorderFolders={handleReorderFolders}
          onDropItems={(folder, ids) => moveLogosToFolder(ids, folder)}
        />
      ) : undefined,
    component: (
      <>
        <EmailDialog
          open={emailDialogOpen}
          setOpen={setEmailDialogOpen}
          email={approvalEmail}
          onEmailChange={setApprovalEmail}
          onSubmit={handleSendPreviewWithEmail}
        />
        {enviroment.multiDeleteLogosEnabled && (
          <MultiDeleteLogosDialogs
            step={multiDeleteStep}
            deletable={deletableSelected}
            skippedCount={skippedForDeleteCount}
            isDeleting={isDeletingSelected}
            progress={multiDeleteProgress}
            onCancel={() => {
              if (!isDeletingSelected) setMultiDeleteStep(0);
            }}
            onContinue={() => setMultiDeleteStep(2)}
            onConfirm={() => {
              executeMultiDelete().then();
            }}
          />
        )}
      </>
    ),
  };
}

const getKey = (logo: BaseLogo) => logo.id;

// Column headers live in a plain constants file (logoColumnsConfig.ts) with no
// React context, so they are translated here at the render site. Keys map the
// exact English displayValue; unknown values (incl. the empty hidden-price
// header, whose truthiness is visibility logic in logo-list-item) pass through.
const COLUMN_HEADER_KEYS: Record<string, string> = {
  "Image": "image",
  "NO.": "logoList.colNo",
  "Title": "title",
  "Colors": "logoList.colorsHeader",
  "Customer": "customer",
  "Vendor": "vendor",
  "Logo Price": "logoList.colLogoPrice",
  "Customer Price": "logoList.colCustomerPrice",
  "In Storage": "logoList.colInStorage",
  "Creation Date": "logoList.colCreationDate",
  "Sales price": "salesMarkup.badge",
  "Status": "status",
  "Action": "action",
};

function translateColumnHeader(displayValue: string): string {
  const key = COLUMN_HEADER_KEYS[displayValue];
  return key ? i18next.t(key) : displayValue;
}

function buildLogoFilterRecipes(
  profile: Profile,
  transforMethods: TransferMethod[],
  vendor?: Organization[],
  customers?: Organization[],
) {
  const filters: FilterRecipe<any>[] = [];
  const { options: logoOptions } = useLogoContext();
  const { t, i18n } = useTranslation();

  if (profile.userOrganizationInformations.role >= Role.Vendor && customers) {
    filters.push({
      options: customers,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: t("customer"),
    } as FilterRecipe<Organization>);
  }

  filters.push({
    options: [
      LogoState.Approved,
      LogoState.Rejected,
      LogoState.Pending,
      LogoState.Draft,
    ],
    key: "stateFilter",
    getOptionValue: (org) => org as string,
    // Flag logoListI18nEnabled: translated option TEXT only - the value
    // (the API's SortableState name) is untouched (logoListI18n.ts).
    getOptionDisplay: isLogoListI18nOn()
      ? (org) => getStatusLabel(org as string, t)
      : (org) => org as string,
    defaultTitle: t("status"),
  } as FilterRecipe<LogoState>);

  filters.push({
    options: logoOptions.transferLogoTypes,
    key: "typeFilter",
    getOptionValue: (o) => o.typeKey,
    getOptionDisplay: isLogoListI18nOn()
      ? (o) => getTypeLabel(o, t)
      : (o) => o.nickname,
    defaultTitle: t("type"),
  } as FilterRecipe<TransferLogoType>);

  filters.push({
    // Flag on: every screen-print variant is ONE option whose value is
    // rewritten to typeFilter=DIREK TRYK in handleFilterChange.
    options: mergeScreenPrintMethods(transforMethods, t),
    key: LOGO_METHOD_FILTER_KEY,
    getOptionValue: (org) => org.key,
    getOptionDisplay: isLogoListI18nOn()
      ? (org) => getMethodLabel(org, t)
      : (org) => org.displayName,
    defaultTitle: t("logoList.method"),
  } as FilterRecipe<TransferMethod>);

  // Logo Classification Filter - hardcoded definitions (value = classification
  // code used by the filter; name is display-only and therefore translated)
  const logoClassifications = [
    { value: 3, name: t("logo") },
    { value: 2, name: t("name") },
    { value: 6, name: t("logoList.numberset") },
    { value: 4, name: t("logoList.clubLogo") },
  ];

  // Owner decision: chain logos (classification 11 — logos from Danish
  // sports chains) are only surfaced when the UI language is Danish.
  if (isDanishLanguage(i18n.language)) {
    logoClassifications.push({ value: 11, name: t("logoList.chainLogo") });
  }

  filters.push({
    options: logoClassifications,
    key: "classification",
    getOptionValue: (o) => o.value.toString(),
    getOptionDisplay: (o) => o.name,
    defaultTitle: t("logoList.logoType"),
  } as FilterRecipe<{ value: number; name: string }>);

  // Free-text "Color" filter (flag logoColorFilterEnabled): last in the row,
  // and only with the flag on - off, the recipe list is today's.
  const colorFilter = buildLogoColorFilterRecipe(
    enviroment.logoColorFilterEnabled,
    (key) => t(key),
  );
  if (colorFilter) {
    filters.push(colorFilter as FilterRecipe<any>);
  }

  return filters;
}

// NOTE: called conditionally from ListHeader.tsx, so no hooks allowed here -
// translate via the i18next instance directly (evaluated on every call).
export function buildAdditionalLogoFilterRecipes(
  sizes: number[],
  colors: Color[],
  fonts: Font[],
) {
  const filters: FilterRecipe<any>[] = [];

  filters.push({
    options: sizes,
    key: "HeightMm",
    getOptionValue: (o) => o,
    getOptionDisplay: (o) => o.toString(),
    defaultTitle: i18next.t("logoList.size"),
  } as FilterRecipe<number>);

  filters.push({
    options: colors,
    key: "Color.Id",
    getOptionValue: (o) => o.id,
    // Translated label for the white/black system colors, raw nickname||name
    // for everything else (no hooks here, so translate via i18next directly).
    getOptionDisplay: (o) => colorPickerLabel(o, (key) => i18next.t(key)),
    defaultTitle: i18next.t("logoList.color"),
  } as FilterRecipe<Color>);

  filters.push({
    options: fonts,
    key: "Font",
    getOptionValue: (o) => o.key,
    getOptionDisplay: (o) => o.name,
    defaultTitle: i18next.t("font"),
  } as FilterRecipe<Font>);

  return filters;
}

function logoRowBgColor(logo: BaseLogo) {
  if (logo.logoType !== LogoType.ImageLogo) {
    return "bg-row-standard";
  }

  const imageLogo = logo as ImageLogo;

  if (imageLogo.status === LogoEditStatus.Draft) {
    return "bg-row-draft";
  }

  if (imageLogo.status === LogoEditStatus.Done) {
    return "bg-row-standard";
  }

  switch (GetOverallApprovalStatus(imageLogo.approval!)) {
    case ApprovalStatus.Rejected:
      return "bg-row-warn";
    case ApprovalStatus.Approved:
      return "bg-row-standard";
    case ApprovalStatus.Pending:
      return "bg-row-pending";
  }
}

export function getLogoState(logo: BaseLogo): LogoState {
  const imageLogo = logo as ImageLogo;

  if (imageLogo.status === LogoEditStatus.Draft) {
    return LogoState.Draft;
  }

  if (imageLogo.status === LogoEditStatus.Done) {
    return LogoState.Approved;
  }

  if (imageLogo.status === LogoEditStatus.Rejected) {
    return LogoState.Rejected;
  }

  if (imageLogo.approval === undefined) {
    return LogoState.Undefined;
  }

  switch (GetOverallApprovalStatus(imageLogo.approval!)) {
    case ApprovalStatus.Rejected:
      return LogoState.Rejected;
    case ApprovalStatus.Approved:
      return LogoState.Approved;
    case ApprovalStatus.Pending:
      return LogoState.Pending;
  }
}

export enum LogoState {
  Draft = "Draft",
  Committed = "Committed",
  Rejected = "Rejected",
  Pending = "Pending",
  Approved = "Approved",
  Undefined = "Undefined",
}
